import React from "react";

import { Box, Typography, useTheme } from "@mui/material";

import { RushaMark } from "@shell/components/RushaMark";

import {
  AuthFooter,
  AuthGraphicSection,
  AuthPageRoot,
  ContentWrapper,
  FormArea,
  FormCard,
  FormPanel,
  FormPanelGrid,
  GraphicGrid,
  LogoRow,
  Subtitle,
  Tagline,
  TrustBar,
  Wordmark,
} from "./Layout.styled";

interface LayoutProps {
  Component: React.FC;
  showGraphic?: boolean;
}

const BrandPanel: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <AuthGraphicSection>
      <GraphicGrid />
      <ContentWrapper>
        {/* Unified composition: R mark hub + pipeline satellites + stats */}
        <Box
          component="img"
          src={
            isDark
              ? "/brand/composition-dark.svg"
              : "/brand/composition-light.svg"
          }
          alt="Rusha — Engineering Control Plane"
          sx={{ maxWidth: 440, width: "100%" }}
        />

        <Box sx={{ mt: 2 }}>
          <Wordmark variant="h2">rusha</Wordmark>
          <Subtitle variant="caption">ENGINEERING CONTROL PLANE</Subtitle>
        </Box>

        <Tagline variant="body1">
          Ship infrastructure, not tickets.
          <br />
          Multi-cloud orchestration from commit to production.
        </Tagline>
      </ContentWrapper>

      {/* Trust bar */}
      <TrustBar>
        TRUSTED BY&nbsp; amband.co.ke &nbsp; oneworldanalytics.com &nbsp;
        tierraviva.ai
      </TrustBar>
    </AuthGraphicSection>
  );
};

export const Layout: React.FC<LayoutProps> = ({
  Component,
  showGraphic = true,
}) => {
  if (!showGraphic) {
    return (
      <AuthPageRoot>
        <FormPanel sx={{ flex: 1, borderRight: "none" }}>
          <FormPanelGrid />
          <LogoRow>
            <RushaMark size={28} />
            <Box
              component="span"
              sx={{
                color: "text.primary",
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: 1,
                letterSpacing: "normal",
              }}
            >
              rusha
            </Box>
          </LogoRow>
          <FormArea>
            <FormCard>
              <Component />
            </FormCard>
          </FormArea>
          <AuthFooter>© 2026 Rusha — Privacy — Terms</AuthFooter>
        </FormPanel>
      </AuthPageRoot>
    );
  }

  return (
    <AuthPageRoot>
      {/* Left: Form panel */}
      <FormPanel>
        <FormPanelGrid />
        <LogoRow>
          <RushaMark size={28} />
          <Typography
            sx={{
              color: "text.primary",
              fontWeight: 700,
              fontSize: "1.25rem",
              letterSpacing: "-0.02em",
            }}
          >
            rusha
          </Typography>
        </LogoRow>
        <FormArea>
          <FormCard>
            <Component />
          </FormCard>
        </FormArea>
        <AuthFooter>© 2026 Rusha — Privacy — Terms</AuthFooter>
      </FormPanel>

      {/* Right: Brand panel (hidden on mobile) */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flex: 1,
          flexDirection: "column",
        }}
      >
        <BrandPanel />
      </Box>
    </AuthPageRoot>
  );
};
